import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from 'react-leaflet';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MapPin, Search } from 'lucide-react';
import L from 'leaflet';
import { toast } from 'sonner';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom red marker for center
const centerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom blue marker for cities
const cityIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [15, 25],
  iconAnchor: [7, 25],
  popupAnchor: [1, -20],
  shadowSize: [25, 25]
});

// Custom green marker for manually-selected cities
const selectedCityIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [22, 36],
  iconAnchor: [11, 36],
  popupAnchor: [1, -30],
  shadowSize: [36, 36]
});

// Component to auto-fit bounds
function MapBounds({ center, radiusKm }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      if (radiusKm === 0) {
        // Just zoom to the city
        map.setView([center.lat, center.lng], 12);
      } else {
        // Fit bounds to include the circle
        const radiusInDegrees = radiusKm / 111;
        const bounds = [
          [center.lat - radiusInDegrees, center.lng - radiusInDegrees],
          [center.lat + radiusInDegrees, center.lng + radiusInDegrees]
        ];
        map.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  }, [map, center, radiusKm]);
  
  return null;
}

const AVAILABLE_CITIES = [
  "Aachen", "Augsburg", "Berlin", "Bergisch Gladbach", "Bielefeld", "Bochum",
  "Bonn", "Bottrop", "Braunschweig", "Bremen", "Bremerhaven", "Celle",
  "Chemnitz", "Cottbus", "Cuxhaven", "Darmstadt", "Delmenhorst", "Dortmund",
  "Dresden", "Duisburg", "Düsseldorf", "Emden", "Erfurt", "Essen",
  "Flensburg", "Frankfurt", "Freiburg", "Fürth", "Gelsenkirchen", "Gera",
  "Göttingen", "Greifswald", "Gütersloh", "Hagen", "Halle", "Hamburg",
  "Hamm", "Hannover", "Heidelberg", "Herne", "Hildesheim", "Ingolstadt",
  "Jena", "Karlsruhe", "Kassel", "Kiel", "Koblenz", "Köln", "Krefeld",
  "Leipzig", "Leverkusen", "Lübeck", "Ludwigshafen", "Lüneburg", "Magdeburg",
  "Mainz", "Mannheim", "Moers", "Mönchengladbach", "Mülheim", "München",
  "Münster", "Neubrandenburg", "Neumünster", "Neuss", "Nürnberg", "Oberhausen",
  "Oldenburg", "Osnabrück", "Paderborn", "Pforzheim", "Potsdam", "Recklinghausen",
  "Regensburg", "Remscheid", "Reutlingen", "Rostock", "Saarbrücken", "Salzgitter",
  "Schwerin", "Siegen", "Solingen", "Stade", "Stralsund", "Stuttgart",
  "Trier", "Ulm", "Wiesbaden", "Wilhelmshaven", "Wismar", "Wolfsburg",
  "Wuppertal", "Würzburg"
].sort();

const GERMAN_CITIES_COORDS = {
  // Top 20
  "Berlin": { lat: 52.5200, lng: 13.4050 },
  "Hamburg": { lat: 53.5511, lng: 9.9937 },
  "München": { lat: 48.1351, lng: 11.5820 },
  "Köln": { lat: 50.9375, lng: 6.9603 },
  "Frankfurt": { lat: 50.1109, lng: 8.6821 },
  "Stuttgart": { lat: 48.7758, lng: 9.1829 },
  "Düsseldorf": { lat: 51.2277, lng: 6.7735 },
  "Dortmund": { lat: 51.5136, lng: 7.4653 },
  "Essen": { lat: 51.4556, lng: 7.0116 },
  "Leipzig": { lat: 51.3397, lng: 12.3731 },
  "Bremen": { lat: 53.0793, lng: 8.8017 },
  "Dresden": { lat: 51.0504, lng: 13.7373 },
  "Hannover": { lat: 52.3759, lng: 9.7320 },
  "Nürnberg": { lat: 49.4521, lng: 11.0767 },
  "Duisburg": { lat: 51.4344, lng: 6.7623 },
  "Bochum": { lat: 51.4818, lng: 7.2162 },
  "Wuppertal": { lat: 51.2562, lng: 7.1508 },
  "Bielefeld": { lat: 52.0302, lng: 8.5325 },
  "Bonn": { lat: 50.7374, lng: 7.0982 },
  "Münster": { lat: 51.9607, lng: 7.6261 },
  
  // 21-60
  "Karlsruhe": { lat: 49.0069, lng: 8.4037 },
  "Mannheim": { lat: 49.4875, lng: 8.4660 },
  "Augsburg": { lat: 48.3705, lng: 10.8978 },
  "Wiesbaden": { lat: 50.0826, lng: 8.2400 },
  "Gelsenkirchen": { lat: 51.5177, lng: 7.0857 },
  "Mönchengladbach": { lat: 51.1805, lng: 6.4428 },
  "Braunschweig": { lat: 52.2689, lng: 10.5268 },
  "Chemnitz": { lat: 50.8278, lng: 12.9214 },
  "Kiel": { lat: 54.3233, lng: 10.1228 },
  "Aachen": { lat: 50.7753, lng: 6.0839 },
  "Halle": { lat: 51.4825, lng: 11.9705 },
  "Magdeburg": { lat: 52.1205, lng: 11.6276 },
  "Freiburg": { lat: 47.9990, lng: 7.8421 },
  "Krefeld": { lat: 51.3388, lng: 6.5853 },
  "Lübeck": { lat: 53.8655, lng: 10.6866 },
  "Oberhausen": { lat: 51.4963, lng: 6.8515 },
  "Erfurt": { lat: 50.9848, lng: 11.0299 },
  "Mainz": { lat: 49.9929, lng: 8.2473 },
  "Rostock": { lat: 54.0887, lng: 12.1402 },
  "Kassel": { lat: 51.3127, lng: 9.4797 },
  "Hagen": { lat: 51.3671, lng: 7.4632 },
  "Hamm": { lat: 51.6739, lng: 7.8150 },
  "Saarbrücken": { lat: 49.2401, lng: 6.9969 },
  "Mülheim": { lat: 51.4274, lng: 6.8825 },
  "Potsdam": { lat: 52.3906, lng: 13.0645 },
  "Ludwigshafen": { lat: 49.4774, lng: 8.4452 },
  "Oldenburg": { lat: 53.1435, lng: 8.2146 },
  "Leverkusen": { lat: 51.0459, lng: 6.9888 },
  "Osnabrück": { lat: 52.2799, lng: 8.0472 },
  "Solingen": { lat: 51.1652, lng: 7.0670 },
  "Heidelberg": { lat: 49.3988, lng: 8.6724 },
  "Herne": { lat: 51.5386, lng: 7.2256 },
  "Neuss": { lat: 51.2047, lng: 6.6883 },
  "Darmstadt": { lat: 49.8728, lng: 8.6512 },
  "Paderborn": { lat: 51.7189, lng: 8.7575 },
  "Regensburg": { lat: 49.0134, lng: 12.1016 },
  "Ingolstadt": { lat: 48.7665, lng: 11.4257 },
  "Würzburg": { lat: 49.7913, lng: 9.9534 },
  "Fürth": { lat: 49.4778, lng: 10.9897 },
  "Wolfsburg": { lat: 52.4227, lng: 10.7865 },
  
  // Norddeutschland & wichtig für Logistik
  "Flensburg": { lat: 54.7836, lng: 9.4321 },
  "Neumünster": { lat: 54.0725, lng: 9.9839 },
  "Bremerhaven": { lat: 53.5396, lng: 8.5809 },
  "Wilhelmshaven": { lat: 53.5312, lng: 8.1080 },
  "Emden": { lat: 53.3594, lng: 7.2060 },
  "Cuxhaven": { lat: 53.8617, lng: 8.6945 },
  "Stade": { lat: 53.5939, lng: 9.4747 },
  "Lüneburg": { lat: 53.2502, lng: 10.4142 },
  "Celle": { lat: 52.6240, lng: 10.0807 },
  "Göttingen": { lat: 51.5412, lng: 9.9158 },
  "Hildesheim": { lat: 52.1513, lng: 9.9502 },
  "Salzgitter": { lat: 52.1535, lng: 10.3565 },
  "Delmenhorst": { lat: 53.0508, lng: 8.6311 },
  "Schwerin": { lat: 53.6355, lng: 11.4014 },
  "Neubrandenburg": { lat: 53.5574, lng: 13.2610 },
  "Stralsund": { lat: 54.3133, lng: 13.0889 },
  "Greifswald": { lat: 54.0955, lng: 13.3807 },
  "Wismar": { lat: 53.8925, lng: 11.4648 },
  
  // Weitere wichtige Städte
  "Ulm": { lat: 48.4011, lng: 9.9876 },
  "Pforzheim": { lat: 48.8921, lng: 8.6945 },
  "Reutlingen": { lat: 48.4914, lng: 9.2044 },
  "Koblenz": { lat: 50.3569, lng: 7.5890 },
  "Jena": { lat: 50.9272, lng: 11.5892 },
  "Gera": { lat: 50.8806, lng: 12.0834 },
  "Cottbus": { lat: 51.7606, lng: 14.3340 },
  "Siegen": { lat: 50.8748, lng: 8.0243 },
  "Trier": { lat: 49.7596, lng: 6.6441 },
  "Bergisch Gladbach": { lat: 50.9859, lng: 7.1345 },
  "Bottrop": { lat: 51.5247, lng: 6.9285 },
  "Recklinghausen": { lat: 51.6139, lng: 7.1974 },
  "Remscheid": { lat: 51.1791, lng: 7.1894 },
  "Moers": { lat: 51.4508, lng: 6.6268 },
  "Gütersloh": { lat: 51.9068, lng: 8.3858 }
};

const RADIUS_OPTIONS = [25, 50, 75];

const RadiusSearchInline = ({ onSearch }) => {
  const [selectedCity, setSelectedCity] = useState("Berlin");
  const [selectedRadius, setSelectedRadius] = useState(null); // null = sadece şehir
  const [loading, setLoading] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [manuallySelectedCities, setManuallySelectedCities] = useState([]); // Manuel seçilen şehirler
  const [cityQuery, setCityQuery] = useState('');
  const [customCity, setCustomCity] = useState(null);
  const [searchingCity, setSearchingCity] = useState(false);

  // Auto-load on mount
  useEffect(() => {
    fetchNearbyCities();
  }, [selectedCity, selectedRadius, customCity]);

  const fetchNearbyCities = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const activeCityName = customCity ? customCity.name : selectedCity;
      const activeCoords = customCity
        ? { lat: customCity.lat, lng: customCity.lng }
        : (GERMAN_CITIES_COORDS[selectedCity] || GERMAN_CITIES_COORDS["Berlin"]);

      // If no radius selected, just search in the city itself
      if (!selectedRadius) {
        setMapData({
          center: activeCoords,
          center_city: activeCityName,
          radius_km: 0,
          cities: [{
            name: `${activeCityName}, Germany`,  // Format with ", Germany"
            ...activeCoords
          }]
        });
        setLoading(false);
        return;
      }
      
      // Otherwise, fetch nearby cities
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nearby-cities?center_city=${encodeURIComponent(activeCityName)}&radius_km=${selectedRadius}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error('GeoNames API Fehler');
      }

      const data = await response.json();
      setMapData(data);
      toast.success(`${data.cities.length} Städte gefunden!`);
    } catch (error) {
      console.error('Nearby cities failed:', error);
      toast.error('❌ Fehler beim Laden der Städte');
    } finally {
      setLoading(false);
    }
  };

  const handleCitySearch = async () => {
    const q = cityQuery.trim();
    if (!q) return;
    setSearchingCity(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&countrycodes=de&limit=1&q=${encodeURIComponent(q)}`,
        { headers: { 'Accept-Language': 'de' } }
      );
      const data = await res.json();
      if (data && data.length > 0) {
        const place = data[0];
        const name = (place.display_name || q).split(',')[0].trim();
        setCustomCity({ name, lat: parseFloat(place.lat), lng: parseFloat(place.lon) });
        setManuallySelectedCities([]);
        toast.success(`📍 ${name} gefunden!`);
      } else {
        toast.error('Stadt nicht gefunden');
      }
    } catch (e) {
      toast.error('Stadt-Suche fehlgeschlagen');
    } finally {
      setSearchingCity(false);
    }
  };

  const handleStartSearch = () => {
    if (mapData && mapData.cities.length > 0) {
      // Format city names as "CityName, Germany" for backend
      // Check if already formatted to avoid double ", Germany"
      const cityNames = mapData.cities.map(c => 
        c.name.endsWith(', Germany') ? c.name : `${c.name}, Germany`
      );
      const radiusValue = mapData.radius_km || 0;
      
      console.log('🗺️ CITY NAMES DEBUG:', cityNames.slice(0, 5), `... (${cityNames.length} total)`);
      
      onSearch(cityNames, radiusValue);
    }
  };
  
  const handleManualSearch = () => {
    if (manuallySelectedCities.length > 0) {
      const cityNames = manuallySelectedCities.map(c => `${c}, Germany`);
      console.log('🎯 MANUAL SELECTION:', cityNames);
      toast.success(`${cityNames.length} şehir manuel seçildi!`);
      onSearch(cityNames, 0);
    } else {
      toast.error('Lütfen haritadan en az 1 şehir seçin!');
    }
  };
  
  const toggleCitySelection = (cityName) => {
    setManuallySelectedCities(prev => {
      if (prev.includes(cityName)) {
        toast.info(`${cityName} entfernt`);
        return prev.filter(c => c !== cityName);
      } else {
        toast.success(`${cityName} hinzugefügt`);
        return [...prev, cityName];
      }
    });
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Left Panel - Controls */}
      <div className="col-span-4 space-y-4">
        {/* Free-text city search (any German city/town via OpenStreetMap) */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-2 block">
            Beliebige Stadt suchen (Name oder PLZ)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={cityQuery}
              onChange={(e) => setCityQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCitySearch()}
              placeholder="z.B. Buxtehude, 21614..."
              data-testid="city-geocode-input"
              className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
            />
            <Button
              onClick={handleCitySearch}
              disabled={searchingCity || !cityQuery.trim()}
              size="sm"
              data-testid="city-geocode-search-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {searchingCity ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          {customCity && (
            <div className="flex items-center justify-between mt-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg">
              <span className="text-xs font-semibold text-purple-800">📍 {customCity.name}</span>
              <button
                onClick={() => { setCustomCity(null); setCityQuery(''); }}
                className="text-xs text-red-600 hover:text-red-800 font-semibold"
                data-testid="clear-custom-city-btn"
              >
                ✕ Entfernen
              </button>
            </div>
          )}
        </div>

        {/* Center City Selection */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-2 block">
            Stadt wählen
          </label>
          <select
            value={selectedCity}
            onChange={(e) => { setSelectedCity(e.target.value); setCustomCity(null); }}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            {AVAILABLE_CITIES.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        {/* Radius Selection - Optional */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-2 block">
            Radius (Optional)
          </label>
          <div className="grid grid-cols-5 gap-2">
            <button
              onClick={() => setSelectedRadius(null)}
              className={`p-2 rounded-lg border-2 font-semibold transition-all text-xs ${
                selectedRadius === null
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-700 border-gray-300 hover:border-blue-400'
              }`}
            >
              Nein
            </button>
            {RADIUS_OPTIONS.map(radius => (
              <button
                key={radius}
                onClick={() => setSelectedRadius(radius)}
                className={`p-2 rounded-lg border-2 font-semibold transition-all text-xs ${
                  selectedRadius === radius
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-slate-700 border-gray-300 hover:border-purple-400'
                }`}
              >
                {radius}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {selectedRadius ? `${selectedRadius} km Radius` : 'Nur Stadt-Zentrum'}
          </p>
        </div>

        {/* Results & Manual Selection Info */}
        {mapData && mapData.cities && (
          <>
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="pt-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Stadt:</span>
                    <span className="font-semibold">{mapData.center_city}</span>
                  </div>
                  {selectedRadius && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Radius:</span>
                        <span className="font-semibold">{mapData.radius_km} km</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Gefunden:</span>
                        <Badge className="bg-purple-600">{mapData.cities.length} Städte</Badge>
                      </div>
                    </>
                  )}
                  {manuallySelectedCities.length > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Manuell gewählt:</span>
                      <Badge className="bg-blue-600">{manuallySelectedCities.length} Städte</Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Manual Selection Hint */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="pt-3 pb-3">
                <p className="text-xs text-blue-800">
                  💡 <strong>Tipp:</strong> Klicken Sie auf Marker um Städte manuell auszuwählen. Grün = ausgewählt. Unten den Button „Suche X ausgewählte Städte" verwenden.
                </p>
              </CardContent>
            </Card>
          </>
        )}

        {/* Auto Search Button (Radius-based) */}
        {selectedRadius && !manuallySelectedCities.length && (
          <Button
            onClick={handleStartSearch}
            disabled={!mapData || loading}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
            size="lg"
          >
            <Search className="mr-2 h-5 w-5" />
            Alle {mapData?.cities.length || 0} Städte durchsuchen
          </Button>
        )}
        
        {/* Single City Search Button (No Radius) */}
        {!selectedRadius && !manuallySelectedCities.length && (
          <Button
            onClick={handleStartSearch}
            disabled={!mapData || loading}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
            size="lg"
          >
            <Search className="mr-2 h-5 w-5" />
            Suche in {selectedCity}
          </Button>
        )}
        
        {/* Manual Selection Search Button */}
        {manuallySelectedCities.length > 0 && (
          <Button
            onClick={handleManualSearch}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold"
            size="lg"
          >
            <Search className="mr-2 h-5 w-5" />
            Suche {manuallySelectedCities.length} ausgewählte Städte
          </Button>
        )}
        
        {/* Clear Manual Selection */}
        {manuallySelectedCities.length > 0 && (
          <Button
            onClick={() => setManuallySelectedCities([])}
            variant="outline"
            className="w-full"
            size="sm"
          >
            Auswahl löschen ({manuallySelectedCities.length})
          </Button>
        )}
      </div>

      {/* Right Panel - Map */}
      <div className="col-span-8">
        {mapData ? (
          <MapContainer
            center={[mapData.center.lat, mapData.center.lng]}
            zoom={8}
            style={{ height: '400px', width: '100%', borderRadius: '8px' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Center Marker */}
            <Marker position={[mapData.center.lat, mapData.center.lng]} icon={centerIcon}>
              <Popup>
                <div className="text-center">
                  <strong className="text-red-600">{mapData.center_city}</strong>
                  <br />
                  <span className="text-xs">Zentrum</span>
                </div>
              </Popup>
            </Marker>

            {/* Radius Circle - Only if radius selected */}
            {mapData.radius_km > 0 && (
              <Circle
                center={[mapData.center.lat, mapData.center.lng]}
                radius={mapData.radius_km * 1000}
                pathOptions={{
                  color: '#9333ea',
                  fillColor: '#9333ea',
                  fillOpacity: 0.1,
                  weight: 2
                }}
              />
            )}

            {/* City Markers - Only if radius selected */}
            {mapData.radius_km > 0 && mapData.cities.slice(0, 100).map((city, idx) => {
              const cityBase = city.name.split(',')[0].trim();
              const isSelected = manuallySelectedCities.includes(cityBase);
              return (
                <Marker 
                  key={idx} 
                  position={[city.lat, city.lng]} 
                  icon={isSelected ? selectedCityIcon : cityIcon}
                  eventHandlers={{
                    click: () => {
                      toggleCitySelection(cityBase);
                    }
                  }}
                >
                  <Popup>
                    <div className="text-center">
                      <strong className="text-xs">{cityBase}</strong>
                      <br />
                      {isSelected ? (
                        <span className="text-xs text-green-700 font-semibold">✓ Ausgewählt</span>
                      ) : (
                        <span className="text-xs text-slate-500">Nicht ausgewählt</span>
                      )}
                      <br />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCitySelection(cityBase);
                        }}
                        className={`mt-2 text-xs px-2 py-1 rounded text-white ${isSelected ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                      >
                        {isSelected ? '✗ Entfernen' : '+ Auswählen'}
                      </button>
                      <br />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSearch([cityBase + ', Germany'], 0);
                        }}
                        className="mt-1 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded"
                      >
                        🔍 Nur hier suchen
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            <MapBounds center={mapData.center} radiusKm={mapData.radius_km} />
          </MapContainer>
        ) : (
          <div className="h-[400px] flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-center">
              <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Karte wird geladen...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RadiusSearchInline;
